// =============================================================================
// CATALOG SECTION COMPONENT (Ecommerce Style)
// =============================================================================

import React, { useState } from 'react';
import { Check, Star, ShoppingCart, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/Button';
import { useShoppingBag } from '@/contexts/ShoppingBagContext';
import { useToast } from '@/contexts/ToastContext';
import type { ContentComponent } from '@/types';
import './PricingSection.scss';

interface PricingSectionProps {
  component: ContentComponent;
}

/**
 * Catalog Section Component
 */
export const PricingSection: React.FC<PricingSectionProps> = ({ component }) => {
  return <CatalogSectionImpl component={component} />;
};

/**
 * Catalog Section Implementation
 */
const CatalogSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const pricing = metadata?.pricing || [];
  const columns = metadata?.columns || 2;
  
  return (
    <section id={id} className="content-section catalog-section">
      <div className="container">
        {title && (
          <div className="section-header">
            <h2>{title}</h2>
            {content && <p>{content}</p>}
          </div>
        )}
        
        <div className={`catalog-grid grid-cols-${columns}`}>
          {pricing.map((category: any, index: number) => (
            <CatalogProductCard key={index} category={category} />
          ))}
        </div>
      </div>
    </section>
  );
};

/**
 * Individual Product Card Component (Category with multiple products)
 */
const CatalogProductCard: React.FC<{ category: any }> = ({ category }) => {
  // Default to montage image, no product selected
  const [selectedProduct, setSelectedProduct] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedSize = category.sizes?.[0] || '';
  const { addItem, openBag } = useShoppingBag();
  const toast = useToast();
  
  const products = category.products || [];
  const currentImage = selectedProduct !== null 
    ? products[selectedProduct]?.image 
    : category.montageImage;
  const currentPrice = selectedProduct !== null 
    ? products[selectedProduct]?.price 
    : null;
  
  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Calculate price range for display when no product is selected
  const getPriceRange = (): string => {
    if (products.length === 0) return '';
    const prices = products.map((p: any) => parseInt(p.price.replace('$', '')));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    return minPrice === maxPrice ? `$${minPrice}` : `$${minPrice} - $${maxPrice}`;
  };

  // Parse price string to cents
  const parsePrice = (priceStr: string): number => {
    const cleaned = priceStr.replace(/[^0-9.]/g, '');
    const dollars = parseFloat(cleaned);
    return Math.round(dollars * 100);
  };

  // Handle adding item to shopping bag
  const handleAddToBag = () => {
    // If no product selected, select first one or show message
    if (selectedProduct === null) {
      if (products.length > 0) {
        // Auto-select first product
        const productToAdd = products[0];
        const price = parsePrice(productToAdd.price);
        
        addItem({
          categoryName: category.name,
          productName: `${category.name} - Option 1`,
          image: productToAdd.image,
          price: price,
          priceDisplay: productToAdd.price,
          size: selectedSize,
          description: category.description,
          categoryDescription: category.description,
        });
        
        toast.success('Added to shopping bag');
        openBag();
      } else {
        toast.error('Please select a product');
      }
      return;
    }

    // Add selected product to bag
    const product = products[selectedProduct];
    const price = parsePrice(product.price);
    
    addItem({
      categoryName: category.name,
      productName: `${category.name} - Option ${selectedProduct + 1}`,
      image: product.image,
      price: price,
      priceDisplay: product.price,
      size: selectedSize,
      description: category.description,
      categoryDescription: category.description,
    });
    
    toast.success('Added to shopping bag');
    openBag();
  };
  
  return (
    <article className={`catalog-product-card ${category.popular ? 'popular' : ''} ${isExpanded ? 'expanded' : ''}`}>
      {category.popular && (
        <div className="product-badge">
          <Star size={16} />
          <span>Most Popular</span>
        </div>
      )}
      
      {/* Product Image Gallery */}
      <div className="product-image-container">
        <div className="product-main-image">
          <img 
            src={currentImage} 
            alt={selectedProduct !== null ? `${category.name} ${selectedProduct + 1}` : `${category.name} Collection`}
            loading="lazy"
          />
          {/* Expand/collapse toggle button */}
          <button
            className="product-image-fullscreen-toggle"
            onClick={handleToggleExpand}
            aria-label={isExpanded ? 'Collapse image' : 'Expand image'}
            type="button"
            title={isExpanded ? 'Collapse image' : 'Expand image'}
          >
            {isExpanded ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
        </div>
        {products.length > 0 && (
          <div className="product-image-thumbnails">
            {products.map((product: any, idx: number) => (
              <button
                key={idx}
                type="button"
                className={`thumbnail ${selectedProduct === idx ? 'active' : ''}`}
                onClick={() => setSelectedProduct(idx)}
                aria-label={`View ${category.name} ${idx + 1}`}
              >
                <img src={product.image} alt={`${category.name} ${idx + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Product Info - Hidden when expanded */}
      {!isExpanded && (
        <div className="product-info">
        <div className="product-header">
          <h3 className="product-name">{category.name}</h3>
        </div>
        
        {category.description && (
          <p className="product-description">{category.description}</p>
        )}
        
        {/* Size Display */}
        {category.sizes && category.sizes.length > 0 && (
          <div className="product-size-selector">
            <span className="size-label">Size</span>
            <span className="size-display">{category.sizes[0]}</span>
          </div>
        )}
        
        {/* Product Features */}
        {category.features && category.features.length > 0 && (
          <div className="product-features">
            <ul className="features-list">
              {category.features.slice(0, 4).map((feature: string, featureIndex: number) => (
                <li key={featureIndex} className="feature-item">
                  <Check size={14} className="feature-check" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Add to Bag Button */}
        <div className="product-actions">
          <Button
            variant="primary"
            size="lg"
            onClick={handleAddToBag}
            className="buy-now-button"
            disabled={!currentPrice && products.length === 0}
          >
            <ShoppingCart size={18} />
            Add to Bag
          </Button>
        </div>
      </div>
      )}
    </article>
  );
};

export { CatalogSectionImpl };
